import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronsLeftIcon, ChevronsRightIcon, FileIcon, MailIcon, MessageCircleIcon, PaperclipIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteNote, updateLeadStage, updateNoteColor } from '../../services/leadService';
import { NoteColor } from '../../types';
import { NOTE_COLORS, noteColor } from './noteColors';
import { addFile, deleteFile } from '../../services/fileService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { dash, resolveLeadContact } from '../../utils/leadFields';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { Textarea } from '../ui/Textarea';
import { Avatar } from '../ui/Avatar';
import { SourceBadge } from './SourceBadge';
import { StageBadge } from './StageBadge';
interface LeadDrawerProps {
  leadId: string | null;
  onClose: () => void;
}
type PanelState = 'compacto' | 'ampliado';

/** Sección plegable dentro de la ficha del lead. */
function Section({
  title,
  count,
  open,
  onToggle,
  children
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-border" data-id="element-105" data-mp="30vptmjbaf1o8">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-id="element-106" data-mp="238zdqbju9j6t">
        <span className="text-sm font-semibold text-foreground" data-id="element-107" data-mp="287u0fokxfrw7">
          {title}
          {count !== undefined && <span className="ml-2 rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-108" data-mp="2d4key7h32ffi">
              {count}
            </span>}
        </span>
        <ChevronDownIcon className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-109" data-mp="1kh6ngow5mc2s" />
      </button>
      {open && <div className="border-t border-border p-4" data-id="element-110" data-mp="1hflch5yrfhx8">{children}</div>}
    </section>;
}

/**
 * Panel lateral del lead con tres estados: cerrado, compacto y ampliado.
 * Las secciones son plegables y su estado se mantiene durante la sesión.
 */
export function LeadDrawer({
  leadId,
  onClose
}: LeadDrawerProps) {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    sources,
    notes,
    history,
    forms,
    landings,
    files
  } = useWorkspace();
  const fileRef = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<PanelState>('compacto');
  const [essentials, setEssentials] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({
    info: true,
    respuestas: false,
    notas: true,
    historial: false,
    archivos: false
  });
  const [noteFeedback, setNoteFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [noteFilter, setNoteFilter] = useState<'all' | NoteColor>('all');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteDraftColor, setNoteDraftColor] = useState<NoteColor>('gray');
  const [savingNote, setSavingNote] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    if (leadId) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [leadId, onClose]);
  const lead = leads.find(l => l.id === leadId);
  if (!lead) return null;
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  // Misma resolución de contacto que la tabla y el embudo.
  const contact = resolveLeadContact(lead);
  const stage = stages.find(s => s.id === lead.stageId);
  const leadNotes = notes.filter(n => n.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const visibleNotes = noteFilter === 'all' ? leadNotes : leadNotes.filter(n => (n.color ?? 'gray') === noteFilter);
  const leadFiles = files.filter(f => f.leadId === lead.id);
  const canEdit = hasPermission('leads.edit');
  const expanded = panel === 'ampliado';
  const essentialInfo: [string, string][] = [['Empresa', dash(contact.company)], ['Teléfono', dash(contact.phone)], ['Tipo de servicio', lead.service || '—'], ['Presupuesto disponible', lead.budget || '—']];
  const fullInfo: [string, string][] = [...essentialInfo, ['Email', dash(contact.email)], ['Instagram', lead.instagram || '—'], ['Ciudad', [lead.city, lead.country].filter(Boolean).join(', ') || '—'], ['Web', lead.website || '—'], ['Inversión actual en publicidad', lead.adBudget || '—'], ['Campaña', lead.origin.campaign || '—'], ['Landing', landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'], ['Formulario', forms.find(f => f.id === lead.origin.formId)?.name ?? '—'], ['Fecha de entrada', formatDateTime(lead.createdAt)], ['Último contacto', lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar']];
  const info = essentials ? essentialInfo : fullInfo;
  /** Guarda la nota escrita en el editor directo de la ficha. */
  async function saveDraftNote() {
    if (!lead || !noteDraft.trim()) return;
    setSavingNote(true);
    setNoteFeedback(null);
    try {
      await addNote(lead.organizationId, lead.id, user?.id ?? 'user', authorName, noteDraft.trim(), undefined, noteDraftColor);
      setNoteDraft('');
      setNoteFeedback({
        ok: true,
        text: 'Nota guardada.'
      });
    } catch (error) {
      setNoteFeedback({
        ok: false,
        text: error instanceof Error ? error.message : 'No se ha podido guardar la nota.'
      });
    } finally {
      setSavingNote(false);
    }
  }
  function toggle(key: string) {
    setOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }
  const phoneDigits = contact.phone.replace(/\D/g, '');
  return <>
      <div className="fixed inset-0 z-40 bg-navy/25 backdrop-blur-[2px] lg:bg-navy/10" onClick={onClose} aria-hidden="true" data-id="element-111" data-mp="3r71lzujwqsqp" />
      <aside role="dialog" aria-label={`Ficha de ${fullName(lead.firstName, lead.lastName)}`} className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl transition-[max-width] duration-300 ${expanded ? 'lg:max-w-[680px]' : 'lg:max-w-[440px]'}`} data-id="element-112" data-mp="3n6hm4j7y9b0v">
        {/* Cabecera */}
        <header className="flex items-start gap-3 border-b border-border p-4 sm:p-5" data-id="element-113" data-mp="9pqsi67gtvp6">
          <div className="min-w-0 flex-1" data-id="element-114" data-mp="9s36y9e0lnxh">
            <p className="truncate text-base font-semibold text-navy" data-id="element-115" data-mp="npru5nvk175p">
              {contact.name || 'Contacto sin nombre'}
            </p>
            <p className="truncate text-sm text-muted-foreground" data-id="element-116" data-mp="ir81z5qdaz4w">
              {contact.company}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-117" data-mp="2pipq3skmofuv">
              <StageBadge stage={stage} stages={canEdit ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-118" data-mp="2kjctwh7v9wfm" />
              <SourceBadge source={lead.origin.source} sources={sources} data-id="element-119" data-mp="14yuc098wp4us" />
            </div>
          </div>
          <div className="flex flex-none items-center gap-1" data-id="element-120" data-mp="r7p8gw2f3sf6">
            <button type="button" onClick={() => setPanel(expanded ? 'compacto' : 'ampliado')} aria-label={expanded ? 'Contraer panel' : 'Ampliar panel'} className="hidden rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground lg:block" data-id="element-121" data-mp="r774tedfebx8">
              {expanded ? <ChevronsRightIcon className="h-4 w-4" data-id="element-122" data-mp="m9e24vbc001p" /> : <ChevronsLeftIcon className="h-4 w-4" data-id="element-123" data-mp="m5zw9d3gl8pi" />}
            </button>
            <button type="button" onClick={onClose} aria-label="Cerrar panel" className="rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground" data-id="element-124" data-mp="8a5zttjvgbta">
              <XIcon className="h-4 w-4" data-id="element-125" data-mp="22mckrk2ki6ys" />
            </button>
          </div>
        </header>

        {/* Acciones rápidas */}
        <div className="grid grid-cols-3 gap-2 border-b border-border p-4 sm:p-5" data-id="element-126" data-mp="26no3d5n0xrxh">
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-128" data-mp="pf7xys5qyzik" />} onClick={() => window.open(`https://wa.me/${phoneDigits}`, '_blank', 'noopener')} data-id="element-127" data-mp="udj6mzkb33z7">
            WhatsApp
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MailIcon className="h-4 w-4" data-id="element-130" data-mp="fpoebka7n1mn" />} onClick={() => window.open(`mailto:${contact.email}`)} data-id="element-129" data-mp="fpy4p8z63asv">
            Email
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-132" data-mp="6rcrltcgsnzh" />} onClick={() => window.open(`tel:${contact.phone}`)} data-id="element-131" data-mp="6pjhu56k1nn9">
            Llamar
          </Button>
        </div>

        {/* Contenido */}
        <div className="flex-1 space-y-3 overflow-y-auto p-4 sm:p-5" data-id="element-133" data-mp="3sy162pfraw5c">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-surface px-4 py-3" data-id="element-134" data-mp="1w4g8a1n08gy2">
            <Switch id="essentials" label="Mostrar solo lo esencial" checked={essentials} onChange={setEssentials} data-id="element-135" data-mp="1r4v85nw3m2jr" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2" data-id="element-136" data-mp="1zlvi5px1xhoj">
            <Select id="drawer-stage" label="Estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-137" data-mp="23n78xyrnw8yb" />
            <Select id="drawer-owner" label="Comercial" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
            value: m.userId,
            label: fullName(m.firstName, m.lastName)
          }))} value={lead.assignedTo ?? ''} onChange={e => {
            const member = members.find(m => m.userId === e.target.value);
            assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
          }} data-id="element-138" data-mp="2dfmqww2ykxj7" />
          </div>

          <Section title="Información principal" open={open.info} onToggle={() => toggle('info')} data-id="element-139" data-mp="3627jfzv67foc">
            <dl className="divide-y divide-border" data-id="element-140" data-mp="z67qvz5df5u">
              {info.map(([label, value]) => <div key={label} className="flex items-start justify-between gap-4 py-2.5" data-id="element-141" data-mp="x24mg92ok6m">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-142" data-mp="5w4zj7vc9a0v">
                    {label}
                  </dt>
                  <dd className="max-w-[60%] break-words text-right text-sm text-foreground" data-id="element-143" data-mp="er2v6mpvf0vw">
                    {value}
                  </dd>
                </div>)}
            </dl>
          </Section>

          {!essentials && <Section title="Respuestas del formulario" count={lead.answers.length} open={open.respuestas} onToggle={() => toggle('respuestas')} data-id="element-144" data-mp="3j5sx0idw4mdb">
              {lead.answers.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-145" data-mp="3s57onl5sn2ze">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border" data-id="element-146" data-mp="12f407lnubz6k">
                  {lead.answers.map(answer => <div key={answer.questionId} className="py-2.5" data-id="element-147" data-mp="17efix07rpeqs">
                      <dt className="text-sm font-medium text-foreground" data-id="element-148" data-mp="1hj5orh6al30a">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-149" data-mp="1cl3tzyzi7f8z">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </Section>}

          <Section title="Notas" count={leadNotes.length} open={open.notas} onToggle={() => toggle('notas')} data-id="element-150" data-mp="13rifmgr94003">
            {/* Editor directo: se escribe aquí mismo, sin pasos intermedios. */}
            {hasPermission('notes.edit') && <div className={`mb-4 space-y-2 rounded-xl border border-border p-3 ${noteColor(noteDraftColor).bg}`} data-id="element-151" data-mp="tvxjgz8v5ty9">
                <Textarea id="note-inline" aria-label="Escribe tu nota" rows={3} placeholder="Escribe tu nota…" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} data-id="element-152" data-mp="kt66gv0caq4i" />
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-153" data-mp="2j66i1mnv4e6l">
                  <div className="flex items-center gap-1.5" data-id="element-154" data-mp="2f7pax34fz3p6">
                    {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={`Color ${tone.label}`} aria-pressed={noteDraftColor === tone.value} onClick={() => setNoteDraftColor(tone.value)} className={`h-8 w-8 rounded-full ${tone.dot} transition-transform hover:scale-110 ${noteDraftColor === tone.value ? 'ring-2 ring-foreground ring-offset-2' : 'opacity-50'}`} data-id="element-155" data-mp="cip8c8474dhs" />)}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-156" data-mp="vc1kc0wb2nuz">
                    {noteDraft.trim() && <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={() => {
                  setNoteDraft('');
                  setNoteFeedback(null);
                }} data-id="element-157" data-mp="10a35crdakx93">
                        Cancelar
                      </Button>}
                    <Button size="sm" className="min-h-[44px]" loading={savingNote} disabled={!noteDraft.trim()} onClick={saveDraftNote} data-id="element-158" data-mp="razl3k2e2bt0">
                      Guardar nota
                    </Button>
                  </div>
                </div>
                {noteFeedback && <p role="status" className={`text-sm ${noteFeedback.ok ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-159" data-mp="33gsceydt5l7d">
                    {noteFeedback.text}
                  </p>}
              </div>}
            {/* Filtro por color */}
            <div className="mb-3 flex flex-wrap gap-1.5" data-id="element-160" data-mp="3de6tn07k2e0q">
              {([{
              value: 'all',
              label: 'Todas'
            }, ...NOTE_COLORS.map(c => ({
              value: c.value,
              label: `${c.label}s`
            }))] as {
              value: 'all' | NoteColor;
              label: string;
            }[]).map(option => <button key={option.value} type="button" aria-pressed={noteFilter === option.value} onClick={() => setNoteFilter(option.value)} className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${noteFilter === option.value ? 'bg-foreground text-background' : 'bg-surface text-muted-foreground hover:text-foreground'}`} data-id="element-161" data-mp="2uiqfov13xepw">
                  {option.label}
                </button>)}
            </div>
            {visibleNotes.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-162" data-mp="10934zjufwgp1">
                Todavía no hay notas ni comentarios.
              </p> : <ol className="space-y-3" data-id="element-163" data-mp="19bugtkr89eh7">
                {visibleNotes.map(note => <li key={note.id} className={`rounded-xl border border-border border-l-4 p-3 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-164" data-mp="199hqaj7elvsj">
                    <div className="flex items-center gap-2" data-id="element-165" data-mp="1j0cevu84h7h5">
                      <Avatar name={note.authorName} size="sm" data-id="element-166" data-mp="1o0i8vh1v762e" />
                      <div className="min-w-0 flex-1" data-id="element-167" data-mp="1nynoq4yf3hzd">
                        {note.title && <p className="truncate text-sm font-semibold text-foreground" data-id="element-168" data-mp="1t0muflhmqqzh">
                            {note.title}
                          </p>}
                        <p className="text-xs text-muted-foreground" data-id="element-169" data-mp="3uqy9gzxxa7tr">
                          {note.authorName} · {formatDateTime(note.createdAt)}
                        </p>
                      </div>
                      {note.authorId === user?.id && hasPermission('notes.edit') && <button type="button" aria-label="Eliminar nota" onClick={() => deleteNote(note.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-170" data-mp="3rabthc1qk240">
                          <Trash2Icon className="h-4 w-4" data-id="element-171" data-mp="3hjh7m8sej4j3" />
                        </button>}
                    </div>
                    {/* Cambiar el color de una nota ya guardada */}
                    {hasPermission('notes.edit') && <div className="mt-2 flex gap-1.5" data-id="element-172" data-mp="e0ee718alowy">
                        {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={`Marcar como ${tone.label}`} aria-pressed={note.color === tone.value} onClick={() => updateNoteColor(note.id, tone.value)} className={`h-6 w-6 rounded-full ${tone.dot} transition-transform hover:scale-110 ${note.color === tone.value ? 'ring-2 ring-foreground ring-offset-2' : 'opacity-50'}`} data-id="element-173" data-mp="9477v7bzibeh" />)}
                      </div>}
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground" data-id="element-174" data-mp="2krnckvlc05ub">
                      {note.body}
                    </p>
                  </li>)}
              </ol>}
          </Section>

          {!essentials && <Section title="Historial y actividad" count={leadHistory.length} open={open.historial} onToggle={() => toggle('historial')} data-id="element-175" data-mp="2d9fri8iuqq4x">
              <ol className="space-y-3" data-id="element-176" data-mp="2h9zq7gj2q1t6">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3" data-id="element-177" data-mp="23jw7gzjekq8h">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-178" data-mp="23ib9t11skr6e" />
                    <div data-id="element-179" data-mp="28bxiyj8qycxg">
                      <p className="text-sm text-foreground" data-id="element-180" data-mp="28c5wmdcp1m9p">{event.description}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-181" data-mp="1tjdglktf89h2">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </Section>}

          {!essentials && <Section title="Archivos" count={leadFiles.length} open={open.archivos} onToggle={() => toggle('archivos')} data-id="element-182" data-mp="1pjbpu6p850gk">
              <Button variant="secondary" size="sm" className="mb-3 w-full" leftIcon={<PaperclipIcon className="h-4 w-4" data-id="element-184" data-mp="110je266ep89i" />} onClick={() => fileRef.current?.click()} data-id="element-183" data-mp="1605ueohj74mj">
                Adjuntar archivo
              </Button>
              <input ref={fileRef} type="file" className="sr-only" onChange={e => {
            const file = e.target.files?.[0];
            if (file && lead) addFile(lead.organizationId, lead.id, file, authorName);
          }} data-id="element-185" data-mp="1axec6nyrp6n9" />
              {leadFiles.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-186" data-mp="1jxkklvflzgfk">
                  No hay archivos adjuntos.
                </p> : <ul className="space-y-2" data-id="element-187" data-mp="1yotggma3ty15">
                  {leadFiles.map(file => <li key={file.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-id="element-188" data-mp="1toyrwpdsxkj9">
                      <FileIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-189" data-mp="27noom3wpli73" />
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground" data-id="element-190" data-mp="22q59z2w3n3em">
                        {file.name}
                      </span>
                      <button type="button" aria-label="Eliminar archivo" onClick={() => deleteFile(file.id)} className="rounded-md p-1.5 text-muted-foreground hover:text-red-600" data-id="element-191" data-mp="3ez30u9y3f5be">
                        <Trash2Icon className="h-4 w-4" data-id="element-192" data-mp="3o0akblvya1lf" />
                      </button>
                    </li>)}
                </ul>}
            </Section>}
        </div>
      </aside>
    </>;
}